import { useMemo } from 'react'
import { DisplayPriceGettingSpecificPriceParams } from '../../../../baseComponents/Displays/DisplayPrice'
import { permissionCheck } from '../../../../baseComponents/PermissionChecker/function'
import { TwinTableLink } from '../../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderBoolean, CustomRenderImageToolTip, DefaultTableCustomRender, DeleteRow, EditRow } from '../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { dictionaryComplexFromJsonArr } from '../../../../utils/globals/dictionary'
import useProductActiveDictionary from '../../../../utils/hooks/product/useProductActiveDictionary'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { CategoriesPayload } from '../../../../utils/reducers/products/categories'
import { SpecificPriceRulesPayload } from '../../../../utils/reducers/products/specificPriceRules'
import { ProductExtendedTyLong } from '../../types'
import { OnRowClick } from '../../../../baseComponents/TwinTable/VirtualTableListing/types'
import locations from '../../../../utils/globals/locations'

interface TableMaterialsProps extends TableComponentProps { 
    specificPriceRules: SpecificPriceRulesPayload
    categories: CategoriesPayload
    onRowClick?: OnRowClick
}

const TableMaterials: React.FC<TableMaterialsProps> = ({ setTableInstance, userPermissions, setOpened, categories, specificPriceRules, onRowClick}) => {
    const { t } = useTwinTranslation()
    const { productActiveDict } = useProductActiveDictionary()
    const dictionaryCategoriesMaterial = dictionaryComplexFromJsonArr(categories?.organized[1].ogCategory, 'id')
    const defaultSpecificPriceRuleId = useMemo(() => {
        const specificPriceRulesSorted = specificPriceRules?.arrSorted || []
        return specificPriceRulesSorted?.[0]?.id
    }, [specificPriceRules?.arrSorted])

    const columns: ColumnTableSchema<ProductExtendedTyLong> = [
        {
            id: 'ProductImages.url',
            dataKey: 'ProductImages.url',
            label: t('image', 'Imagen'),
            width: 75,
            customRender: (parameterValue, allRowData) => <CustomRenderImageToolTip className='table_products_image' value={parameterValue} allRowData={allRowData} />
        },
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            sortable: true,
            width: 75
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('reference', 'Referencia') }
            },
            minWidth: 135
        },
        {
            id: 'name',
            dataKey: 'ProductLangs.name',
            label: t('name', 'Nombre'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            minWidth: 130
        },
        {
            id: 'price',
            dataKey: 'SpecificPriceRuleId',
            label: (props) => {
                const myValue = parseInt(props.selectedFilterValue || props.value || defaultSpecificPriceRuleId)
                return <span>{t('price', 'Precio') + ' (' + (specificPriceRules?.dictionary?.[myValue]?.name || t('notSelectedPriceRule', 'Sin tarifa selec.')) + ')'} </span>
            },
            simpleLabel: t('price', 'Precio'),
            minWidth: 150,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { items: specificPriceRules?.dictionary || {}, label: t('specificPriceRule', 'Tarifa') }
            },
            generalSearchable: false,
            customRender: (_parameterValue, allRowData) => {
                return <DisplayPriceGettingSpecificPriceParams value={allRowData?.calculated_price} CurrencyId={specificPriceRules?.dictionary[allRowData.SpecificPriceRuleId]?.CurrencyId || ''} with_vat={false} specificPriceRules={specificPriceRules} SpecificPriceRuleId={defaultSpecificPriceRuleId} />
            }
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { items: productActiveDict, label: t('active', 'Activo') }
            },
            generalSearchable: false
        },
        {
            id: 'CategoryId',
            dataKey: 'CategoriesProducts.CategoryId',
            label: t('category', 'Categoría'),
            sortable: true,
            minWidth: 140,
            customRender: (parameterValue) => {
                return <DefaultTableCustomRender value={parameterValue} items={dictionaryCategoriesMaterial} fieldName='CategoryLangs.name' />
            },
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: dictionaryCategoriesMaterial, label: t('category', 'Categoría'), fieldName: 'CategoryLangs.name' }
            },
            generalSearchable: false
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]
    return (
        <TwinTableLink createRowDataLink={[ locations.material, [ '$id', 'main']]} name='materials' columns={columns} getDataFrom='/api/app/product/getAllProductsComplete' extraWhereParams={{ productType: 1 }} extraWhereSubstituibleParams={{ 'SpecificPriceRuleId': defaultSpecificPriceRuleId }} setTableInstance={setTableInstance} onRowClick={onRowClick} />
    )
}

export default TableMaterials